import { useEffect } from "react";

const EDGE_PX = 48;
const MAX_STEP_PX = 24;
const SYNTHETIC_FLAG = "emiGanttAutoScroll";

/**
 * Pixels to scroll per frame for a pointer at x: nothing inside the viewport,
 * ramping up to the max step as the pointer reaches or passes an edge.
 */
export const getEdgeScrollStep = (
	x,
	left,
	right,
	edge = EDGE_PX,
	maxStep = MAX_STEP_PX,
) => {
	const clamp = (value) => Math.min(Math.ceil(value), maxStep);
	if (x < left + edge) return -clamp(((left + edge - x) / edge) * maxStep);
	if (x > right - edge) return clamp(((x - (right - edge)) / edge) * maxStep);
	return 0;
};

/**
 * Scrolls the chart while a dragged bar is held against either edge.
 *
 * The library positions a dragged bar from the pointer's distance to where
 * the drag began, so scrolling the chart alone leaves the bar behind. Every
 * pointer move during a scrolled drag is replaced with one offset by the
 * distance scrolled, which keeps the bar under the pointer.
 */
const useGanttDragAutoScroll = ({ ganttApi, containerRef }) => {
	useEffect(() => {
		if (!ganttApi) return undefined;

		const drag = {
			active: false,
			scrolled: 0,
			clientX: 0,
			clientY: 0,
			target: null,
			frame: null,
		};

		const chart = () => containerRef.current?.querySelector(".wx-chart");

		const dispatchOffsetMove = () => {
			const el = chart();
			if (!el) return;
			const target = el.contains(drag.target) ? drag.target : el;
			const event = new MouseEvent("mousemove", {
				bubbles: true,
				cancelable: true,
				clientX: drag.clientX + drag.scrolled,
				clientY: drag.clientY,
				buttons: 1,
			});
			event[SYNTHETIC_FLAG] = true;
			target.dispatchEvent(event);
		};

		const stop = () => {
			drag.active = false;
			drag.scrolled = 0;
			if (drag.frame) cancelAnimationFrame(drag.frame);
			drag.frame = null;
		};

		const tick = () => {
			drag.frame = null;
			if (!drag.active) return;

			const el = chart();
			if (el) {
				const rect = el.getBoundingClientRect();
				const step = getEdgeScrollStep(drag.clientX, rect.left, rect.right);
				if (step) {
					const before = el.scrollLeft;
					el.scrollLeft = before + step;
					const moved = el.scrollLeft - before;
					if (moved) {
						drag.scrolled += moved;
						dispatchOffsetMove();
					}
				}
			}
			drag.frame = requestAnimationFrame(tick);
		};

		const onMouseMove = (event) => {
			if (event[SYNTHETIC_FLAG]) return;
			drag.clientX = event.clientX;
			drag.clientY = event.clientY;
			drag.target = event.target;
			if (!drag.active || !drag.scrolled) return;

			event.stopImmediatePropagation();
			dispatchOffsetMove();
		};

		const unsubscribeDrag = ganttApi.on("drag-task", (ev) => {
			if (!ev.inProgress) {
				stop();
				return;
			}
			if (!drag.active) {
				drag.active = true;
				drag.frame = requestAnimationFrame(tick);
			}
		});

		const unsubscribeUpdate = ganttApi.on("update-task", stop);

		window.addEventListener("mousemove", onMouseMove, true);
		window.addEventListener("mouseup", stop, true);

		return () => {
			stop();
			window.removeEventListener("mousemove", onMouseMove, true);
			window.removeEventListener("mouseup", stop, true);
			[unsubscribeDrag, unsubscribeUpdate].forEach((cleanup) => {
				if (typeof cleanup === "function") cleanup();
			});
		};
	}, [ganttApi, containerRef]);
};

export default useGanttDragAutoScroll;
